'use client';
/** Plugin browser-UI runtime (plugin platform F0).
 *
 *  A plugin ships ONE built same-origin ESM bundle (served by the daemon on an immutable content-hash
 *  URL, listed by GET /plugins/ui). Loading it here is a SCRIPT TAG, not a bundler import — the URL is
 *  runtime data, and a `<script type="module">` is the one mechanism no bundler rewrites. The bundle
 *  talks back through two window globals installed below:
 *
 *    window.ElowenUiRuntime          — the host API surface (react, curated components, hooks, navigate)
 *    window.__elowenRegisterPluginUi — the bundle's registration call (pages + settings components)
 *
 *  Security model, explicitly: an admin-installed plugin already runs inside the daemon process (full
 *  trust ≈ RCE); its browser bundle is the SMALLER privilege. There is no sandbox here and pretending
 *  otherwise would be theatre — marketplace review is the filter. Bundles are same-origin only, so
 *  cookies and the BFF bearer work unchanged and a future CSP needs nothing beyond `script-src 'self'`. */
import { useDesktopControl } from '../components/desktop/useDesktopControl';
import { useDesktopPreviewState } from '../components/desktop/useDesktopPreviewState';
import { useDesktopStill } from '../components/desktop/useDesktopStill';
import { DesktopPreview } from '../components/desktop/DesktopPreview';
import { ResourceHistoryModal } from '../components/ui/ResourceHistoryModal';
import { SandboxedHtml } from '../components/ui/SandboxedHtml';
import { MasterDetailGrid } from '../components/ui/MasterDetailGrid';
import { MarkdownPreview } from '../components/ui/MarkdownPreview';
import { TokenList } from '../components/ui/TokenList';
import { ImageLightbox } from '../components/ui/ImageLightbox';
import { PdfPreview } from '../components/ui/PdfPreview';
import { ResourceMeters } from '../components/ui/ResourceMeters';
import { RowPicker } from '../components/ui/RowPicker';
import * as React from 'react';
import * as ReactDom from 'react-dom';
import * as JsxRuntime from 'react/jsx-runtime';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePathname, useSearchParams } from 'next/navigation';
import type { ComponentType, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
// The contract (runtime surface, registration shape, Window globals) lives in elowen-plugin-ui-kit —
// the SAME package plugin authors build against — so the two sides cannot drift. Types ONLY: a value
// import would need Turbopack to resolve the symlinked package outside its root, while `import type`
// is erased before bundling. Re-exported below for the app's own consumers.
import type { ElowenUiRuntime, NavigateOptions, PluginUiLocation, PLUGIN_UI_API_VERSION as KIT_API_VERSION, PluginPageProps, PluginUiComponentName, PluginUiRegistration, RichTextEditorProps, TerminalSurfaceComponent, TerminalSurfaceProps } from 'elowen-plugin-ui-kit';
import type { PluginUiListing } from './types';
import { BASE, apiUrl, apiErrorMessage, elowenClient, ElowenApiError } from './elowenClient';
import { openBrainConversationDock, openBrainSessionWindow, startBrainConversation } from './brainDock';
import { Button } from '../components/ui/Button';
import { ConnectionStatus } from '../components/ui/ConnectionStatus';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/shadcn/textarea';
import { Slider } from '../components/ui/Slider';
import { Avatar as UserAvatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { LabelMark, StatusGlyph, StatusMark } from '../components/ui/StatusMark';
import { Tooltip, TooltipAnchor, TooltipContent } from '../components/ui/shadcn/tooltip';
import { useTooltipAnchor } from '../components/ui/shadcn/useTooltipAnchor';
import { AvatarStack } from '../components/ui/AvatarStack';
import { SortableList, useSortableItem } from '../components/ui/Sortable';
import { SortableBoard, SortableBoardItems, useSortableBoardContainer, useSortableBoardItem } from '../components/ui/SortableBoard';
import { Field } from '../components/ui/Field';
import { HelpTip } from '../components/ui/HelpTip';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Toggle } from '../components/ui/Toggle';
import { ModuleHeader } from '../components/ui/ModuleHeader';
import { Segmented } from '../components/ui/Segmented';
import { SelectMenu } from '../components/ui/SelectMenu';
import { EntityList, EntityRow } from '../components/ui/EntityList';
import { CardGrid, CardGridItem } from '../components/ui/CardGrid';
import { CardSelectCheckbox } from '../components/ui/CardSelectCheckbox';
import { SelectionActionBar } from '../components/ui/SelectionActionBar';
import { LoadingState, LoadingLine, ErrorState, EmptyState } from '../components/ui/states';
import { MotionLayoutItem, MotionPresence } from '../components/ui/Motion';
import { SpatialWorkspaceLayout, WorkspaceMetric, WorkspacePage } from '../components/ui/WorkspacePrimitives';
import { WorkspaceShell } from '../components/ui/WorkspaceShell';
import { WorkspaceHero } from '../components/ui/WorkspaceHero';
import { WorkspaceTakeover } from '../components/ui/WorkspaceTakeover';
import { DeckNavigation, SectionDeck } from '../components/ui/SectionDeck';
import { Pager } from '../components/ui/Pager';
import { ResizablePanels } from '../components/ui/ResizablePanels';
import { PageToolbar } from '../components/ui/PageToolbar';
import { PageFilters } from '../components/ui/PageFilters';
import { RegisterSearch } from '../components/ui/RegisterSearch';
import { TimeSeriesChart } from '../components/ui/TimeSeriesChart';
import { ProjectFilterPills } from '../components/ui/ProjectFilterPills';
import { projectName } from '../../packages/plugin-shared/projectChoice.mjs';
import { ProjectIcon } from '../components/ui/ProjectIcon';
import { ControlSurfaceDocument, ControlSurfaceRegister, ControlSurfaceState, ControlSurfaceToolbar } from '../components/ui/ControlSurface';
import { ModelIcon } from '../components/ui/ModelIcon';
import { ToolMark } from '../components/ui/ToolMark';
import { ConversationIcon } from '../components/ui/ConversationIcon';
import { PluginIcon } from '../modules/settings/PluginIcon';
import { AdaptiveBrandMark } from '../components/ui/AdaptiveBrandMark';
import { IconButton } from '../components/ui/IconButton';
import { ActionMenu } from '../components/ui/ActionMenu';
import { ContextMenu, ContextMenuTrigger } from '../components/ui/ContextMenu';
import { uploadProjectFile } from './projectUploads';
import { Progress } from '../components/ui/shadcn/progress';
import { RailDot, RailList, RailRow, RailSection } from '../components/ui/RailPrimitives';
import { RailMenu } from '../components/ui/RailMenu';
import { MarkStrip } from '../components/ui/MarkStrip';
import { HoverCard } from '../components/ui/HoverCard';
import { TaskList, TaskRow } from '../components/ui/TaskList';
import { ShimmerText } from '../components/ui/ShimmerText';
import { PatchView } from '../components/ui/PatchView';
import dynamic from 'next/dynamic';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useOperationDock } from './operationDock';
import { Calendar } from '../components/ui/shadcn/calendar';
import { ManageSelectionModal } from '../components/ui/ManageSelectionModal';
import { SelectionSummary, SummaryChip } from '../components/ui/SelectionSummary';
import { LinkedAccountRow } from '../components/ui/LinkedAccountRow';
import { StatusDot } from '../components/ui/DetailPrimitives';
import { SliderWithReading } from '../components/ui/SliderWithReading';
import { BrainModelField } from '../components/ui/BrainModelField';
import { useAutosaveToast, useOperationFailure, useToast } from '../components/ui/Toast';
import { ChoiceField } from '../components/ui/ChoiceField';
import { Checkbox, CheckboxSlot } from '../components/ui/Checkbox';
import { DateRangeFilter } from '../components/ui/DateRangeFilter';
import { Spinner } from '../components/ui/states';
import { WorkspaceDetailRail } from '../components/ui/WorkspacePrimitives';
import { SettingsDocument, SettingsGroup, SettingsItem, SettingsList, SettingsRow, SettingsState, SettingsValue } from '../components/ui/SettingsSurface';
import { SettingsPresentationContext } from '../components/ui/settingsPresentation';
import { PluginConfigEditor } from '../modules/settings/PluginConfigEditor';
import { usePluginConfigDraft } from './usePluginConfigDraft';
import { MarkdownAssetEditor } from '../modules/settings/MarkdownAssetEditor';
import { compactElapsed, localDateTime, parseTs } from './format';
import { AUTOSAVE_UNCHANGED, useAutoSaveStatus } from './useAutoSaveStatus';
import { SaveConflictError, holdsAnswer } from './saveFeedback';
import { useCopyAction } from './useCopyAction';
import { MentionChip, MentionText, useMentionTokens } from '../components/ui/MentionChip';
import { MentionPicker } from '../components/ui/MentionPicker';
import { useEnvironmentOperation } from './useEnvironmentOperation';
import { useMobile, useMobileViewport } from './useMobile';
import { useNow } from './useNow';
import { defineEditorThemes, editorTheme } from './monaco/oledTheme';
import { interpolate, useTranslation } from './i18n';
import { usePersistentState } from './usePersistentState';
import { useProjectFilter } from './useProjectFilter';
import { useFillHeight } from './useFillHeight';
import {
  usePluginUi, useBrainModels, useUsers,
  useInvalidateConversationLinks, useInvalidatePluginUi, useNotificationDestinations, usePluginSubagents, usePluginDetail,
  useProjects, useProjectGit, useProjectCommits, useProjectEnvironmentState,
  useMe, useModelUsage, useUsageByDay, useUsageByOrigin,
} from './queries';
import {
  useSavePluginSubagent, useDeletePluginSubagent,
} from './mutations';
import { formatBytes, formatDuration, formatTokens, formatUsd } from './format';
import { impersonateUser } from './token';
import { TODO_PREVIEW_ITEMS, todoPreviewItems } from './chatPresentation';
import { cardTasks, cardTasksAddressable } from './railTasks';
import { uncollectedChildResult } from './railSelection';
import { railProcessVisible, formatSpeed } from './displayFormat';
import { buildUsageSummary } from './usageBars';
import { DEFAULT_RANGE, isStoredRange, parseRange, rangeBounds, serializeRange, utcDayWindow, instantRangeBounds } from './dateRange';
import { renderMarkdown } from './markdown';
import { paginateItems } from './paginateItems';
import { workflowCounts, workflowElapsedSeconds, workflowLabel } from './workflowLayout';

// @platform-keep plugin-ui-runtime :: window.ElowenUiRuntime && PLUGIN_UI_API_VERSION && CardGrid && CardGridItem && SelectionActionBar && CardSelectCheckbox
/** Browser-plugin runtime shared by bundled and registry UI consumers. The literal type keeps the host
 * aligned with the kit; a bundle's manifest and registration must declare exactly this generation to mount.
 * Contract history: docs/PLUGIN_DEV.md#browser-contract-history. */
export const PLUGIN_UI_API_VERSION: typeof KIT_API_VERSION = 80;
export type { PluginPageProps, PluginUiRegistration };

/** The page header a plugin surface wears when it is reached as its own page. It is the app's own
 *  workspace header, and the eyebrow is supplied here rather than by each bundle so every plugin page
 *  is labelled the same way in the user's language without shipping the word seven times. A bundle
 *  renders it only for `surface === 'page'`: inside the Settings deck the panel already names the
 *  section, and a second title there would be noise. */
function PluginPageHeader({ title, description, icon, action }: {
  title: string;
  description?: string;
  icon?: LucideIcon;
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  return <WorkspaceHero eyebrow={t.pluginUi.eyebrow} title={title} description={description} icon={icon} action={action} mascot={false} />;
}

/** The frame a plugin's settings section wears per surface, so no bundle has to reimplement it: on a
 *  page it is headed and sits on its own document surface; in the Settings deck the surrounding panel
 *  supplies both, so the children render bare. For a section that already composes its own
 *  SettingsGroups. */
function PluginPageFrame({ surface, title, description, icon, action, plugin, section, children }: {
  surface: 'page' | 'deck';
  /** Omit it and the frame reads the section's label from the manifest listing (already localized), so
   *  the page title has ONE source and cannot drift from the sidebar entry that leads here. */
  title?: string;
  description?: string;
  icon?: LucideIcon;
  action?: ReactNode;
  plugin?: string;
  section?: string;
  children: ReactNode;
}) {
  const { locale } = useTranslation();
  const listing = usePluginUi(locale);
  if (surface === 'deck') return <>{children}</>;
  const entry = plugin === undefined ? undefined : listing.data?.find((p) => p.name === plugin);
  const label = title ?? entry?.settings.find((s) => s.id === section)?.label ?? '';
  return (
    <>
      <PluginPageHeader title={label} description={description} icon={icon} action={action} />
      <SettingsDocument>{children}</SettingsDocument>
    </>
  );
}

/** The common shape: one settings group. The section's own name never appears INSIDE the card. On a page
 *  it is the page header; in the account deck the section rail already carries both the label and the
 *  description, from the very same manifest entry. A native account section renders no card title
 *  either, so a plugin that repeated its own name was the one panel on the page wearing a second
 *  heading. */
function PluginSection({ surface, title, description, icon, action, actions, className, density, children }: {
  surface: 'page' | 'deck';
  title: string;
  description?: string;
  icon?: LucideIcon;
  action?: ReactNode;
  actions?: ReactNode;
  className?: string;
  density?: 'comfortable' | 'compact';
  children: ReactNode;
}) {
  return (
    <PluginPageFrame surface={surface} title={title} description={description} icon={icon} action={action}>
      <SettingsGroup className={className} actions={actions} density={density}>
        {children}
      </SettingsGroup>
    </PluginPageFrame>
  );
}

/** Localized view strings for one plugin's bundle: the /plugins/ui listing's merged `strings` record
 *  (manifest English overlaid with the active locale's i18n `web.strings`).
 *
 *  TOTAL by construction: an unknown key reads as the empty string rather than `undefined`. The record
 *  is empty for the paint or two before the listing resolves, and a view that formats its copy
 *  (`s.someKey.replace('{n}', …)`) would not render a blank label there — it would throw and take the
 *  whole page down over a string that was one round-trip away. Rendering nothing for that instant is
 *  the only sane behaviour.
 *
 *  This is deliberately NOT the place that catches a missing key: a typo or a renamed manifest entry
 *  is caught statically by `tests/contract/pluginBundleStringKeys.test.ts` (the key a bundle reads must
 *  exist in its manifest) and by `scripts/check-languages.mjs` (every manifest key is translated in
 *  every locale). Crashing at runtime would not find them any earlier — it would only find them in
 *  front of a user. */
function usePluginStrings(plugin: string, page?: string): Record<string, string> {
  const { locale } = useTranslation();
  const listing = usePluginUi(locale, page === undefined ? undefined : { plugin, page });
  const strings = listing.data?.find((p) => p.name === plugin)?.strings;
  return React.useMemo(() => new Proxy(strings ?? {}, {
    get: (target, key) => (typeof key === 'string' ? target[key] ?? '' : undefined),
  }), [strings]);
}

type PluginAvatarProps = {
  name?: string;
  src?: string;
  user?: { id: number; username: string; name?: string; avatar?: string };
  size?: number | 'sm' | 'md' | 'lg';
};

/** Public plugin avatar contract. A plugin-owned image wins, then the linked Elowen account avatar,
 * then initials. The host's account Avatar stays behind this adapter so directory-only people never
 * have to mimic its internal user shape; its fixed `src` walks that order. The plugin's `name` is the
 * label of every state, so image, upload and monogram share one accessible name. */
function PluginAvatar({ name, src, user, size = 'md' }: PluginAvatarProps) {
  const pixels = typeof size === 'number' ? size : size === 'sm' ? 28 : size === 'lg' ? 44 : 36;
  const label = name?.trim() || user?.name?.trim() || user?.username || '?';
  return <UserAvatar user={user ? { ...user, name: label } : { id: 0, username: label, name: label }} src={src} size={pixels} />;
}

type Navigate = (href: string, options?: NavigateOptions) => void;
let navigateImpl: Navigate = (href, options) => {
  if (options?.replace) window.location.replace(href);
  else window.location.assign(href);
};
/** The shell installs Next navigation, retaining native history for plugin overlays. */
export function setPluginNavigate(fn: Navigate): void { navigateImpl = fn; }

// Next owns pathname and query. Only the hash needs an adapter: native history writes emit no
// hashchange, so the runtime also publishes its own navigation target after invoking the host.
let locationHash = '';
const hashListeners = new Set<() => void>();
function publishLocationHash(hash: string): void {
  if (hash === locationHash) return;
  locationHash = hash;
  for (const listener of hashListeners) listener();
}
function subscribeLocationHash(listener: () => void): () => void {
  const update = () => publishLocationHash(window.location.hash);
  hashListeners.add(listener);
  window.addEventListener('popstate', update);
  window.addEventListener('hashchange', update);
  update();
  return () => {
    hashListeners.delete(listener);
    window.removeEventListener('popstate', update);
    window.removeEventListener('hashchange', update);
  };
}
const locationHashSnapshot = () => locationHash;
const serverHashSnapshot = () => '';
function usePluginLocation(): PluginUiLocation {
  const pathname = usePathname();
  const params = useSearchParams();
  const hash = React.useSyncExternalStore(subscribeLocationHash, locationHashSnapshot, serverHashSnapshot);
  return { pathname, search: params.toString(), hash, query: Object.fromEntries(params.entries()) };
}
function navigate(href: string, options?: NavigateOptions): void {
  navigateImpl(href, options);
  publishLocationHash(new URL(href, window.location.href).hash);
}

/** xterm with its addons is a large dependency that exactly ONE surface wants (an open terminal
 *  window). Importing it statically here would put it in the shell's own bundle, which every page load
 *  pays for whether or not anybody opens a shell, so the runtime publishes a lazily loaded component
 *  with its own Suspense boundary — the shell's app-wide boundary has a null fallback and would hide an
 *  open window while the chunk lands. `release` belongs to the same lazily loaded module, and reaching
 *  it is free once the surface has been drawn at least once.
 *
 *  The cast to a plain function component is what makes the published member checkable against the kit:
 *  `dynamic()` answers a `ComponentType`, whose class arm cannot satisfy `ComponentType<never>` in the
 *  runtime-wide assertion below (a class carries `defaultProps`), while the component it really is has a
 *  call signature like every other published primitive here. */
const loadTerminalSurface = () => import('../components/terminal/TerminalSurface');
const TerminalSurface: TerminalSurfaceComponent = Object.assign(
  dynamic(() => loadTerminalSurface().then(module => module.TerminalSurface), {
    ssr: false,
    loading: () => <span className="flex h-full items-center justify-center"><Spinner size="lg" /></span>,
  }) as React.FC<TerminalSurfaceProps>,
  {
    release: (sessionKey: string): void => {
      void loadTerminalSurface().then(module => module.TerminalSurface.release(sessionKey));
    },
  },
);

/** The editor engine (Tiptap on ProseMirror) is the same kind of dependency: only a composer wants it, so
 *  it is a lazily loaded chunk rather than part of every page's shell bundle. The placeholder holds the
 *  writing area's minimum height, so the frame does not jump when the chunk lands. */
const RichTextEditor = dynamic(() => import('../components/ui/RichTextEditor').then(module => module.RichTextEditor), {
  ssr: false,
  loading: () => <span className="flex min-h-32 flex-1 items-center justify-center"><Spinner size="lg" /></span>,
}) as React.FC<RichTextEditorProps>;

const registrations = new Map<string, PluginUiRegistration>();
const pendingLoads = new Map<string, Promise<PluginUiRegistration | null>>();

/** Install the window globals exactly once. Idempotent — called from every bundle load, and
 *  exported for tests that render plugin-bundle views against the real runtime surface. */
export function ensurePluginUiRuntime(): void {
  if (typeof window === 'undefined' || window.ElowenUiRuntime) return;
  locationHash = window.location.hash;
  window.ElowenUiRuntime = {
    apiVersion: PLUGIN_UI_API_VERSION,
    react: React,
    reactDom: ReactDom,
    jsxRuntime: JsxRuntime,
    // @platform-keep plugin-ui-primitives :: PatchView && WorkspaceShell, WorkspaceHero && Pager, RegisterSearch && WorkspaceTakeover
    // Generic UI platform for future github/sandblox bundles; zero callers for any one primitive is expected.
    // Growing this list is cheap; shrinking it is a breaking change.
    // `ConstellationScope` left with the orbital settings rendering it existed to switch on, and
    // `TerminalModal` + the `openTerminalWindow` helper left with the browser terminal they opened. The
    // API version deliberately did NOT move for any of them: no bundle in this repo, the registry or the
    // Chetty fork ever named one, so nothing a released plugin can ask for changed. Withdrawing a
    // primitive some bundle DOES reference has to bump the version instead — though note the version is
    // a compatibility CEILING (`entry.apiVersion <= host`), so it can announce an ADDITION and cannot
    // express a removal at all; that is exactly why a referenced primitive must not be withdrawn.
    // API 45 withdrew every member no bundled, registry or installed bundle named (see the version note).
    // `DataTableSelectCell` left the same way once the memory register, its last caller, became cards.
    // API 72 withdrew the table register itself (`DataTable`, `DataTableRow`, `DataTableCell`,
    // `DataTableSortCell`, `DataTableChevronCell`): registers are cards (owner's rule, 22 Oct 2026), and
    // every bundled and registry register draws `CardGrid` instead. Installed bundles that still name
    // them must be updated in the same release (see the version note).
    components: {
      Button, ConnectionStatus, Input, Textarea, Slider, Avatar: PluginAvatar, AvatarStack, Badge, StatusGlyph, StatusMark, LabelMark, Tooltip, TooltipAnchor, TooltipContent, Field, HelpTip, Modal, ModalBody, ModalFooter,
      Toggle, ModuleHeader, Segmented, SelectMenu, EntityList, EntityRow, CardGrid, CardGridItem, CardSelectCheckbox, SelectionActionBar, LoadingState, LoadingLine, ErrorState, EmptyState,
      MotionLayoutItem, MotionPresence, SpatialWorkspaceLayout, WorkspaceMetric,
      // Page chrome a plugin page needs to look like every built-in workspace, not like a bundle that
      // rebuilt the layout for itself.
      WorkspacePage, PluginPageHeader, PluginSection, ProjectFilterPills, ProjectIcon,
      ControlSurfaceDocument, ControlSurfaceRegister, ControlSurfaceState, ControlSurfaceToolbar,
      ModelIcon, ToolMark, PluginIcon, ConversationIcon, AdaptiveBrandMark, IconButton, ActionMenu, ContextMenu,
      // API 71: the one right-click / Menu key / touch long-press opener of `ContextMenu`.
      ContextMenuTrigger,
      SortableList, SortableBoard, SortableBoardItems,
      ConfirmDialog,
      SettingsPresentationContext, SettingsDocument, SettingsGroup, SettingsRow, SettingsValue, SettingsState, SettingsList, SettingsItem, SliderWithReading, TimeSeriesChart, PluginConfigEditor, ChoiceField,
      // The moved settings-deck editors' primitives (cronjob's jobs editor and friends).
      ManageSelectionModal, SelectionSummary, BrainModelField, MarkdownAssetEditor,
      // API 57 published the state dot; API 58 withdrew PropertyGroup, PropertyRow and DetailBlock once
      // every bundle in this repository and the registry drew its window panels from the Settings
      // primitives above (SettingsValue, SettingsState, SettingsList, SettingsItem, SliderWithReading).
      StatusDot,
      // The canonical month/date grid (API 17), the real shadcn Calendar on react-day-picker. A bundle
      // cannot rebuild one without shipping a second copy of React or a second date engine; what a
      // plugin composes from props is only its own pressing: which month is open, which days are
      // selected/enabled, and what a day renders inside itself.
      Calendar,
      // A connector identity, shaped like the chat platforms it sits between. Both are here so a plugin
      // cannot approximate either one: the drawer row it hangs in and the chip it contributes to the
      // closed summary are the SAME components the host draws for Discord and friends, so "looks the
      // same" is a fact about the code rather than a resemblance someone has to maintain by eye.
      LinkedAccountRow, SummaryChip,
      Checkbox, CheckboxSlot, DateRangeFilter,
      // `Progress` is the plain determinate bar. The rail sections that moved into bundles draw a
      // done/total meter with it, and `components` is typed as an open record, so its absence was not a
      // type error in the bundle — only an undefined element at render time.
      // API 27: the rail grid. A section, its list and the row (static, button or menu trigger) sit on
      // the one lead | text | value grid, so every section — core or bundle — lines its labels and its
      // numbers up with every other; `ShimmerText` marks a live line. `RailSection` draws the heading.
      RailSection, RailList, RailRow, RailDot, ShimmerText,
      // The rail's popover (a panel of rail rows), the mark strip that names a mark on hover, and the
      // hover card for content too long to sit in a row. Published so a bundle's live rows can carry the
      // same controls the host's own rows carry — a stop button, a model mark, a brief.
      RailMenu, MarkStrip, HoverCard,
      // API 28: the checklist on that grid. `TaskList` is the rail's Tasks section body (open work first,
      // "+N more", finished work folded), `TaskRow` one task, also at a card's inline type.
      TaskList, TaskRow,
      Progress, PatchView, Spinner, WorkspaceDetailRail,
      // API 63: the shared xterm surface a plugin draws its own shell session in. Lazily loaded (see the
      // note above), and one member carries the whole contract: the component plus `release`, which is
      // what a window calls when a session ENDS rather than when it is merely minimized.
      TerminalSurface,
      // The canonical page shell, published so a bundle stops reaching for the SpatialWorkspaceLayout
      // alias: the alias only ever built a `register` page, so a plugin whose surface is a settings deck
      // or a single working pane had to approximate the other two variants out of raw markup.
      WorkspaceShell, WorkspaceHero,
      // API 19: the exact responsive deck Settings and Account compose inside PageOverlay. The frame owns
      // the sidebar/phone-strip switch and content scroller; the navigation owns the shared record shapes.
      SectionDeck, DeckNavigation,
      // API 68: the static untrusted-HTML frame, without the interactive view's script bridge.
      SandboxedHtml,
      MasterDetailGrid, MarkdownPreview,
      // API 70: the settings' free-form token editor, with a per-token validator, so a recipient field draws
      // the host's chips rather than a second chips input in its bundle.
      TokenList,
      // API 70: the formatted-text field (closed HTML plus plain text), lazily loaded above.
      RichTextEditor,
      // The full-size picture view chat and Guides already open, and the one PDF view (promoted from the
      // Editor bundle): an attachment preview draws the host's surfaces instead of a second copy of each.
      ImageLightbox, PdfPreview,
      // API 36: the shared desktop tile, enlarge surface and claim/status controls.
      DesktopPreview, ResourceHistoryModal, ResourceMeters,
      // API 40: the anchored single-choice dropdown the host's own ChoiceField and model fields are built on.
      RowPicker,
      // API 77: the chat's `@` mention UX as plain-props primitives - the chip a stored mention reads as,
      // the text with its mentions drawn, and the picker a textarea completes a typed `@` with. A plugin's
      // own editor draws them instead of a second chips grammar; the reasoning, the rows and the words all
      // arrive as props (`MentionChipProps`, `MentionTextProps`, `MentionPickerProps`).
      MentionChip, MentionText, MentionPicker,
      // The full-application takeover a plugin page occupies when its surface IS the whole screen (the
      // editor's fullscreen mode). Published because every hand-rolled version got the same three things
      // wrong — `vh` sizing, a literal z-index in the middle of the overlay scale, and an unlabelled
      // 28px chevron as the only exit — and a bundle cannot reach the overlay machinery any other way.
      WorkspaceTakeover,
      // The register footer and the register toolbar's search field — pieces every plugin register
      // hand-rolled. Five bundles (mcp, cronjob, stats, onedrive, editor) each carried their own pager,
      // and the search field was copy-pasted with a hard `min-w` that pushed its sibling controls out of
      // a narrow toolbar.
      Pager, RegisterSearch,
      // API 70: the draggable split an application page lays its panes out on (Email's sidebar, list and
      // conversation), over the same react-resizable-panels primitive as the chat telemetry rail.
      ResizablePanels,
      // The canonical page toolbar and its condensed filter control. A bundle normally reaches the row
      // through `WorkspaceShell`'s `toolbar` prop — that is what puts a plugin register's search and
      // filters in the same place as every built-in page — but both are published as components too, so
      // a surface that mounts its own shell (or none) can still draw the canonical row rather than the
      // hand-rolled band each register used to carry.
      PageToolbar, PageFilters,
      // `satisfies`, never `as`: the assertion that used to stand here silenced the one check that
      // matters. The kit's `PluginUiComponentName` is what a bundle types its own narrowed runtime
      // against, so publishing a primitive the kit has not heard of — or listing one here that is not
      // published — has to fail on this line rather than in a user's browser. API 50's touched components
      // additionally satisfy their real kit props before the existing runtime-wide assertion.
    } satisfies Record<PluginUiComponentName, ComponentType<never>> & Pick<ElowenUiRuntime['components'], 'MentionChip' | 'MentionText' | 'MentionPicker' | 'AdaptiveBrandMark' | 'ResourceHistoryModal' | 'ResourceMeters' | 'DateRangeFilter' | 'TimeSeriesChart' | 'TerminalSurface' | 'ConnectionStatus' | 'SandboxedHtml' | 'TokenList' | 'Pager' | 'ResizablePanels' | 'RichTextEditor' | 'ImageLightbox' | 'PdfPreview' | 'ContextMenuTrigger' | 'EntityList' | 'EntityRow' | 'WorkspaceMetric' | 'ControlSurfaceState' | 'MasterDetailGrid' | 'MarkdownPreview' | 'Badge' | 'StatusGlyph' | 'StatusMark' | 'AvatarStack' | 'ContextMenu' | 'SortableList' | 'SortableBoard' | 'SortableBoardItems'> as ElowenUiRuntime['components'],
    // React hooks a plugin page may call (safe across the boundary — the bundle runs on the HOST's
    // React instance). The data hooks keep the react-query cache + SSE signal store in the app, so a
    // plugin pages and core surfaces share one cache and one invalidation path.
    hooks: {
      useTooltipAnchor, useDesktopControl, useDesktopPreviewState, useDesktopStill,
      useTranslation, useToast, useAutosaveToast, useOperationFailure, useCopyAction, usePersistentState,
      useBrainModels, useAutoSaveStatus, usePluginStrings,
      useUsers, usePluginConfigDraft,
      useSortableItem, useSortableBoardContainer, useSortableBoardItem,
      useInvalidateConversationLinks, useInvalidatePluginUi, useNotificationDestinations,
      usePluginDetail,
      usePluginSubagents, useSavePluginSubagent, useDeletePluginSubagent,
      useProjects, useProjectGit, useProjectCommits, useProjectEnvironmentState,
      useMentionTokens,
      useMobile, useMobileViewport, useNow, useProjectFilter, useFillHeight,
      useLocation: usePluginLocation,
      useMe, useModelUsage, useUsageByDay, useUsageByOrigin,
      // Raw React Query hooks against the host's one QueryClient. A bundle importing the library itself
      // would get a second context and an empty cache, so every query and mutation path crosses
      // this runtime surface instead.
      useQuery, useMutation, useQueryClient,
      // Following one environment lifecycle operation: one seed read, then the daemon's pushed frames.
      // A bundle cannot subscribe to the SSE bus itself, and reimplementing this as a poll is exactly
      // what the pushed progress replaced.
      useEnvironmentOperation, useOperationDock,
    },
    // Pure helpers shared with plugin bundles (formatting, navigation and error shaping).
    // The unlocalized `formatSpeed` chip was withdrawn here with an earlier release. Since API 75 a bundle
    // must declare exactly this generation to mount, so a removal can no longer reach an unmatched bundle.
    utils: {
      projectName,
      uploadProjectFile,
      // API 55: literal named placeholders, using the host's existing i18n implementation.
      interpolate,
      apiErrorMessage, openBrainSessionWindow,
      workflowCounts, workflowLabel, workflowElapsedSeconds,
      uncollectedChildResult, railProcessVisible,
      compactElapsed, parseTs, localDateTime, AUTOSAVE_UNCHANGED, SaveConflictError, holdsAnswer,
      defineEditorThemes, editorTheme,
      formatBytes, formatDuration, formatTokens,
      // The ONE rendering of a measured generation rate. A bundle that formats its own would print a
      // second rule for the same number — the one the daemon measured.
      formatSpeed,
      buildUsageSummary, elowenClient, ElowenApiError,
      // Persisted calendar filters, their UTC-day interpretation for counter-backed usage views, and
      // validation of fixed instant ranges.
      DEFAULT_RANGE, isStoredRange, parseRange, rangeBounds, serializeRange, utcDayWindow, instantRangeBounds,
      // The host's own markdown pipeline, so a bundle rendering authored notes never ships a second
      // parser or its own sanitizer settings.
      renderMarkdown,
      // The visible-page calculation the host registers use, so a bundle's register pages the same way.
      paginateItems,
      // Which rows a chat card previews, and how many. The todo bundle draws the conversation's card,
      // and a second copy of this rule inside it (a plain `slice(0, 4)`) silently stopped showing the
      // running row once finished work filled the cap. The host card, the CLI panel and the bundle all
      // call this one.
      TODO_PREVIEW_ITEMS, todoPreviewItems,
      // Todo cards and rail slots preserve the same structured owner, blockers and timing facts.
      cardTasks, cardTasksAddressable,
      // The host's own identity transition, so a bundle hands an administrator into another account's
      // session through the ONE implementation rather than restating the `elowen:auth-transition`
      // protocol and the POST around it: a restated copy drifts from the event name and misses the
      // in-flight guard the host refuses a second transition with. A refusal rejects with the daemon's
      // own word ("forbidden"), not with a status line the calling surface would have to invent.
      impersonateUser,
      // The ONE localized USD rendering. A bundle that carries its own copy also carries its own answer
      // to the amount nothing priced, and "$0.00" is the wrong one: the host prints an em dash.
      formatUsd,
    },
    api: elowenClient.api,
    apiUrl,
    navigate,
    openConversationDock: openBrainConversationDock,
    startConversation: async (options) => {
      const session = await startBrainConversation(options);
      navigate('/chat');
      return session;
    },
  };
  window.__elowenRegisterPluginUi = (plugin, registration, page) => { registrations.set(JSON.stringify([plugin, page ?? null]), registration); };
}

/** Link a plugin's own stylesheet into the document, once per URL, and resolve when the browser has
 *  APPLIED it. The app is shipped prebuilt, so its CSS carries only the utilities the host itself uses;
 *  anything else a plugin's markup asks for exists only in this sheet. Insert before the host stylesheet:
 *  older plugins were built into the shared `utilities` layer, and appending one after the host let generic
 *  classes such as grid/flex utilities corrupt every page visited afterwards until reload. New builds use a
 *  dedicated lower layer too, but DOM order keeps already-installed plugin versions safe. An `error` resolves
 *  so a missing sheet degrades to an unstyled page rather than a page that never renders. */
const pendingCss = new Map<string, Promise<void>>();
function ensurePluginCss(cssUrl: string): Promise<void> {
  const href = `${BASE}${cssUrl}`;
  const pending = pendingCss.get(href);
  if (pending) return pending;
  const load = new Promise<void>((done) => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.addEventListener('load', () => done());
    link.addEventListener('error', () => done());
    const hostStyle = document.head.querySelector<HTMLLinkElement | HTMLStyleElement>('link[rel="stylesheet"], style');
    // The shell normally has host CSS by the time a plugin can mount. During early hydration/HMR it may not;
    // placing the link first still keeps it ahead of host styles that Next appends afterwards.
    document.head.insertBefore(link, hostStyle ?? document.head.firstChild);
  });
  pendingCss.set(href, load);
  return load;
}

/** Load a plugin's bundle (once) and return its registration. `null` = the script loaded (or failed)
 *  without registering — the caller renders its unavailable placeholder.
 *
 *  This is the ONLY place a plugin bundle is loaded, so it is also the only place its stylesheet can be
 *  linked. The returned promise waits for BOTH: the caller renders nothing until it settles, and a page
 *  painted before its stylesheet applied is a visible flash of unstyled plugin. */
export function loadPluginUi(plugin: string, url: string, cssUrl?: string, page?: string): Promise<PluginUiRegistration | null> {
  ensurePluginUiRuntime();
  // Keyed by bundle URL and checked BEFORE the registration map: a bundle registers synchronously as it
  // executes, so an already-registered plugin whose sheet is still in flight must keep waiting here.
  const key = JSON.stringify([plugin, page ?? null]);
  const loadKey = JSON.stringify([plugin, page ?? null, url, cssUrl ?? null]);
  const pending = pendingLoads.get(loadKey);
  if (pending) return pending;
  // Started before consulting the registration map: a long-lived page may already hold the plugin's JS
  // registration while a newly advertised stylesheet URL has never been linked in this document.
  const css = cssUrl ? ensurePluginCss(cssUrl) : Promise.resolve();
  const existing = registrations.get(key);
  if (existing) {
    const load = css.then(() => existing);
    pendingLoads.set(loadKey, load);
    return load;
  }
  const script = new Promise<PluginUiRegistration | null>((resolveLoad) => {
    const el = document.createElement('script');
    el.type = 'module';
    el.src = `${BASE}${url}`;
    // A module script executes after load fires; resolve from the registration map either way.
    el.addEventListener('load', () => resolveLoad(registrations.get(key) ?? null));
    el.addEventListener('error', () => resolveLoad(null));
    document.head.appendChild(el);
  });
  const load = Promise.all([script, css]).then(([registration]) => registration);
  pendingLoads.set(loadKey, load);
  return load;
}

/** May a plugin browser bundle mount on THIS host? The ONE place the compatibility rule is applied.
 *
 *  Two facts describe what a bundle needs, and they arrive at different moments. The listing row's
 *  `apiVersion` is the manifest's word, known before anything is fetched. `requiresApiVersion` on the
 *  registration is the bundle's OWN word, and it exists only once its script has run — which is why the
 *  listing cannot be the only gate: a bundle that loads and THEN announces a different contract would
 *  already be running against one this host does not have.
 *
 *  Both must equal `PLUGIN_UI_API_VERSION` exactly. The outcome-channel cut (API 75) removed published
 *  members, so a bundle built for an older host is as unfit as one built for a newer one, and an
 *  unversioned manifest or registration declares nothing it could be trusted on. Every mount path asks
 *  this question before it mounts. A bundle refused here is not an error: the path draws what it draws
 *  when the contribution is absent, and the plugin shows the existing incompatible state until updated.
 *
 *  `registration` is `undefined` while the load is in flight and `null` when it failed or registered
 *  nothing; in both cases the bundle has announced no requirement, so only the manifest's word decides. */
export function pluginUiMayMount(
  entry: Pick<PluginUiListing, 'apiVersion'> | undefined,
  registration?: PluginUiRegistration | null,
): boolean {
  if (entry === undefined || entry.apiVersion !== PLUGIN_UI_API_VERSION) return false;
  return registration === null || registration === undefined
    || registration.requiresApiVersion === PLUGIN_UI_API_VERSION;
}

/** Select the eight component slots declared in the authenticated listing. Operation docks have their own object contract. */
export function pluginSlotEntries(
  listing: readonly PluginUiListing[] | undefined,
  slot: 'chatPickers' | 'chatCards' | 'chatRailSections' | 'chatDock' | 'historyBranches' | 'dashboardMetrics' | 'projectRows' | 'projectEditIcon',
): PluginUiListing[] {
  return (listing ?? []).filter(entry => entry.slots.includes(slot) && pluginUiMayMount(entry));
}

/** Resolve one listing entry into what a plugin surface may mount: `compatible` is `pluginUiMayMount`'s
 *  answer for this entry and the registration it produced, so the generation rule is applied to the listing's
 *  declared version AND to the version the loaded registration itself announces; `registration` is
 *  `undefined` until the bundle's load settles, `null` when it failed or never registered, and the
 *  registration when it may mount — the three-way answer every plugin surface renders, from ONE copy of
 *  the rule.
 *
 *  The caller passes its own `loadPluginUi` import rather than calling it from in here, because the
 *  bundle loader is the seam every plugin-surface suite stubs (vi.mock replaces this module's EXPORT,
 *  which an import inside it would bypass for a real `<script>`): the hook loads through whatever
 *  binding its caller holds, so the stubbed and the shipped path are the same code.
 *
 *  Keyed on WHAT would be loaded, never on the listing row identity: the listing is refetched on every
 *  /events reconnect and plugin event, and a refetch that yields an equal but new object must not restart
 *  the load — a restart unmounted the plugin's component for a moment, which cost a chat dock tile its
 *  takeover lease and its live stream. A genuinely different bundle is answered for THAT bundle
 *  alone: a render already carrying a new one reads `undefined` (loading) synchronously rather than
 *  keeping the previous bundle's component mounted under the new bundle's props, and a load that settles
 *  after its bundle was abandoned never lands at all.
 *
 *  Load a set of current bundle identities through the same injected loader as the single-entry seam.
 *  A changed identity withdraws its old registration during render; abandoned loads cannot settle it. */
export function usePluginUiRegistrations(
  entries: readonly PluginUiListing[],
  load: (plugin: string, url: string, cssUrl?: string, page?: string) => Promise<PluginUiRegistration | null>,
): {
  registrations: { entry: PluginUiListing; compatible: boolean; registration: PluginUiRegistration | null | undefined }[];
  ready: boolean;
} {
  const sourceKey = JSON.stringify(entries.filter(entry => pluginUiMayMount(entry))
    .map(entry => [entry.name, entry.url, entry.cssUrl ?? null, entry.page ?? null]));
  const [loaded, setLoaded] = React.useState<{ sourceKey: string; values: (PluginUiRegistration | null)[] } | null>(null);
  React.useEffect(() => {
    const identities = JSON.parse(sourceKey) as [string, string, string | null, string | null][];
    if (identities.length === 0) return;
    let alive = true;
    void Promise.all(identities.map(([name, url, cssUrl, page]) => page === null ? load(name, url, cssUrl ?? undefined) : load(name, url, cssUrl ?? undefined, page)))
      .then(values => { if (alive) setLoaded({ sourceKey, values }); });
    return () => { alive = false; };
  }, [sourceKey, load]);
  const current = loaded?.sourceKey === sourceKey ? loaded : null;
  return React.useMemo(() => {
    let index = 0;
    const registrations = entries.map(entry => {
      const registration = pluginUiMayMount(entry) ? current?.values[index++] : undefined;
      return { entry, registration, compatible: pluginUiMayMount(entry, registration) };
    });
    return { registrations, ready: sourceKey === '[]' || current !== null };
  }, [entries, current, sourceKey]);
}

export function usePluginUiRegistration(
  entry: PluginUiListing | undefined,
  load: (plugin: string, url: string, cssUrl?: string, page?: string) => Promise<PluginUiRegistration | null>,
): {
  compatible: boolean;
  registration: PluginUiRegistration | null | undefined;
} {
  const { registrations } = usePluginUiRegistrations(entry ? [entry] : [], load);
  const registration = registrations[0]?.registration;
  return { compatible: pluginUiMayMount(entry, registration), registration };
}

/** Match `rest` segments against a registration's route patterns: exact segments beat `:param`
 *  captures, longer patterns beat shorter. Returns the component + captured params, or null. */
export function matchPluginPage(
  pages: Record<string, ComponentType<PluginPageProps>> | undefined,
  rest: string[],
): { Component: ComponentType<PluginPageProps>; params: Record<string, string> } | null {
  if (!pages) return null;
  let best: { Component: ComponentType<PluginPageProps>; params: Record<string, string>; exact: number; len: number } | null = null;
  for (const [pattern, Component] of Object.entries(pages)) {
    const parts = pattern === '' ? [] : pattern.split('/');
    if (parts.length !== rest.length) continue;
    const params: Record<string, string> = {};
    let exact = 0;
    let ok = true;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]!;
      if (part.startsWith(':') && part.length > 1) { params[part.slice(1)] = rest[i]!; continue; }
      if (part !== rest[i]) { ok = false; break; }
      exact += 1;
    }
    if (!ok) continue;
    if (!best || exact > best.exact || (exact === best.exact && parts.length > best.len)) {
      best = { Component, params, exact, len: parts.length };
    }
  }
  return best ? { Component: best.Component, params: best.params } : null;
}
